/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable max-lines */
/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { type AfterViewInit, type OnDestroy, type OnInit, Component, ElementRef, Injector, Input, ViewChild } from '@angular/core';
import { type AbstractControl, type ValidationErrors, Validators } from '@angular/forms';
import {
    type Capacity, type Command, type HttpResponse, type opType,
    type PromptConfigFull, type Rest, CAPACITY_UNIT, Consts, DMValidators,
    FormControl, FormGroup, FormSlideWindowAbstract, HttpService, LayoutComponent, SelectComponent,
    TimeService, UNPLACEHOLDER, UtilService,
} from '@dm/core';
import { SelectOption } from '@dm/core/types';
import { CapacityPolicyE, DefaultFsWorkloadTypeE, FileSystemModeE, INFINITE_NUM_64 } from 'app/common/constants';
import { ScheduleTypeE } from 'app/common/constants/hyper-cdp.const';
import { LicenseKey } from 'app/common/constants/license-key';
import { RemoteExecuteService, SmartGuiService, TaskService, ToolService } from 'app/common/services';
import {
    type AVServer, type CIFSClient, type CIFSShare, type DataTurboClient, type DataTurboShare,
    type FileSystem, type FileSystemResource, type FileSystemResourceCIFS, type FileSystemResourceNFS, type HyperCdpSchedule,
    type NFSClient, type NFSShare, type Quota, type StoragePool, type Vstore, type WorkloadType,
} from 'app/common/types/mo';
import { ProductConfig, SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';
import { WorkModeE } from 'app/feature/protection/config/hyper-metro-domain/hyper-metro-domain.enum';
import { DEFAULT_CDP_SCHEDULE_NAME } from 'app/feature/protection/schedule/hyper-cdp/hyper-cdp.const';
import { type HyperCdpScheduleExtends, ScheduleService } from 'app/feature/protection/schedule/hyper-cdp/schedule/schedule.service';
import { SmartCacheService } from 'app/feature/service/resource-tuning/smart-cache/smart-cache.service';
import { CreateFsScanMissionPolicyCardComponent } from 'app/feature/settings/file-storage-service/antivirus/antivirus-policy/filesystem-scan-mission/create-fs-scan-mission-policy-card/create-fs-scan-mission-policy-card.component';
import { ScanPolicyTypeE } from 'app/feature/settings/file-storage-service/antivirus/antivirus-str.service';
import { StoragePoolService } from 'app/feature/system/storage-pool/storage-pool.service';
import { StoragePoolStrService } from 'app/feature/system/storage-pool/storage-pool-str.service';
import { isUndefined } from 'lodash';
import { type Subscriber, type Subscription, forkJoin, Observable, of } from 'rxjs';
import { catchError, filter, finalize, flatMap, map, switchMap, tap } from 'rxjs/operators';

import { UserQuotaService } from '../../quota/user-quota/user-quota.service';
import { UserQuotaStrService } from '../../quota/user-quota/user-quota-str.service';
import { OffLineFileModeE } from '../../share/cifs-share/cifs-share-str.service';
import { DistAlgE } from '../file-system.enum';
import { FileSystemService } from '../file-system.service';
import { type PoolVersionE, ActionE, FileSystemStrService, FileTaskNameE, NasLockingPolicyModeE, PoolOldVersions, SecurityModeE, VaaiSwitchE } from '../file-system-str.service';
import { type CreateFsVmInitItem, type InitVMData, SmartguiFilesystemReceived } from '../fs-model';
import { UnixPermissionConfigCardComponent } from './unix-permisssion-config-card/unix-permisssion-config-card.component';
import { UnixConfigCardActionTypeE, UnixConfigCardEntryTypeE } from './unix-permisssion-config-card/unix-permisssion-config-card.enum';
import { WormConfigCardComponent } from './worm-config-card/worm-config-card.component';

/**
 * 文件系统创建页面的入口
 */
export enum FileSystemEntryE {
    Main = 'main',
    AuditLog = 'auditLog'
}

@Component({
    selector: 'app-create-file-system',
    templateUrl: './create-file-system.component.html',
    styleUrls: ['./create-file-system.component.scss'],
    providers: [
        StoragePoolService,
        StoragePoolStrService,
        LayoutComponent,
    ],
})
export class CreateFileSystemComponent extends FormSlideWindowAbstract implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('ondemandCard') ondemandCard: CreateFsScanMissionPolicyCardComponent;
    @ViewChild('onaccessCard') onaccessCard: CreateFsScanMissionPolicyCardComponent;
    @ViewChild('vstoreSelect') vstoreSelect: SelectComponent;
    @ViewChild('poolSelect') poolSelect: SelectComponent;
    @ViewChild('cdpScheduleSelect') cdpScheduleSelect: SelectComponent;
    @ViewChild('wormConfig') wormConfig: WormConfigCardComponent;
    @ViewChild('cdpCardDiv') cdpCardDiv: ElementRef;
    @ViewChild('cdpEmptyDiv') cdpEmptyDiv: ElementRef;
    @ViewChild('unixPermissionConfig') unixPermissionConfig: UnixPermissionConfigCardComponent;
    // create as时传入一个文件系统数据作为模板进行创建
    @Input() data: FileSystem;
    @Input() vstore: SelectOption;
    // 由哪个地方入口打开的创建文件系统
    @Input() entry: FileSystemEntryE;
    // 获取文件系统智能推荐数据
    @Input() fsSmartguiData: SmartguiFilesystemReceived;
    // 共享开关状态
    @Input() shareSwitch: { nfs: boolean; cifs: boolean; dataTurbo: boolean } = {
        nfs: true,
        cifs: true,
        dataTurbo: true,
    };
    // 智能推荐的cdp开关
    @Input() cdpSwicth = true;
    // cifs智能推荐数据
    @Input() smartGuiCifs: CIFSShare;
    // nfs智能推荐数据
    @Input() smartGuiNfs: NFSShare;
    @Input() remoteParentId: string;

    ScanPolicyTypeE = ScanPolicyTypeE;
    SWITCH = SWITCH;

    // 容量有偏差tip(转化为扇区不为整数的情况)
    capLossTips: string;

    vstorePairId: string;
    remoteVstoreName: string;
    remoteVstoreId: string;

    closeArgs = {
        refresh: false,
        data: null,
    };
    i18n = this.str.i18n;

    vm: FormGroup;
    errorMessage: any;

    pageReady = false;
    windowVisible = false;
    advanced = false;

    workloadCreateTypeMap = this.str.workloadCreateTypeMap;

    language = this.i18nService.getLanguage();

    /**
     * 创建文件系统的同时创建配额
     */
    createQuotaSwitch = false;
    /**
     * 配额数据
     */
    quotaDatas: Quota[] = [];

    FileSystemEntryE = FileSystemEntryE;

    SecurityModeE = SecurityModeE;

    ProductConfig = ProductConfig;

    fsHyperMetroDomain: FsHyperMetroDomain;
    storagePoolOpts: SelectOption[] = [];
    remoteStoragePoolOpts: SelectOption[] = [];
    // 应用类型
    workloadTypeOpts: SelectOption[] = [];
    // 已选中的应用类型
    selectedWorkloadType: WorkloadType;
    /**
     * 选中的存储池
     */
    selectedStoragePool: StoragePool;
    // 是否是隐藏重删压缩的新融合设备
    isCoverageStorHideDedupeAndCompression = false;

    // 多租户对象下拉列表选项
    vstoreRest: Rest;
    // cdp定时计划rest
    cdpScheduleRest: Rest;
    selectedVstore: SelectOption;
    // 内置CDP计划数据
    defaultCdpSchedule: HyperCdpScheduleExtends;
    defaultScheduleName = DEFAULT_CDP_SCHEDULE_NAME;

    defaultOption: SelectOption;

    // 创建表单初始化数据
    initVMData: InitVMData = {};

    // 任务订阅器
    taskSub$: Subscription;
    loading = false;

    supportGuaranteedCapacity = false;
    supportDedupeAndCompression = false;
    // 是否支持内置CDP计划
    supportCdpSchedule = true;

    // 是否显示变长分片
    showVariableSegment = false;

    smartCachePartionList: SelectOption[] = [];
    capacityPolicyOptions: SelectOption[] = this.str.capacityPolicyOptions;
    CapacityPolicyE = CapacityPolicyE;

    // worm 参数
    wormSwitch: boolean;
    wormClockInited = false;
    clockLoading = false;
    infiniteValue = INFINITE_NUM_64;
    hasWormClockR = false;
    hasWormClockW = false;

    // 审计日志参数
    auditItemOpt = this.str.auditItemsOpts;
    isAuditLogEnabled = true;
    jumpToAuditLog = this.service.jumpToAuditLog;

    // 是否显示无pool提示
    showNoPoolMessageTip = false;

    FeatureConfig = FeatureConfig;
    UnixConfigCardActionTypeE = UnixConfigCardActionTypeE;
    UnixConfigCardEntryTypeE = UnixConfigCardEntryTypeE;

    selectedSCPName: string;

    hasSharePermission = this.permissionService.hasPermission('share_W');
    hasAuditStrategyW = this.permissionService.hasPermission('@audit_strategy_W');
    hasAntivirusW = this.permissionService.hasPermission('antivirus_W');
    hasCdpReadPermission = this.permissionService.hasPermission('@hyper_cdp_R');
    /**
     * 检查worm license
     */
    hasWormLicense = false;

    /**
     * 不可输入小数的容量单位
     */
    notDotUnits = ['Sector', 'MB'];

    minCapacity = 1;
    auditLogCapacity = 5;
    maxCapacity = 32;
    tempVstoreValue: string;

    // 是否展示防病毒
    showAntivirus = false;
    ActionE = ActionE;
    /** 二级存储在审计日志中的额外提示 */
    get notSupportDataTurboTip(): string {
        return this.isAuditLogEnabled ? this.i18n.notSupportDataTurboEnabled : this.i18n.notSupportDataTurboUnenabled;
    }

    get wormSwitchTip(): string {
        return !ProductConfig.isSupportVAAISwitch ? this.i18n.wormSwitchTipFor2910 : this.i18n.wormSwitchExclusiveTip;
    }

    constructor(
        injector: Injector,
        public str: FileSystemStrService,
        public service: FileSystemService,
        public toolService: ToolService,
        public utilService: UtilService,
        private storagePoolService: StoragePoolService,
        private smartguiService: SmartGuiService,
        private taskService: TaskService,
        private remoteExecuteService: RemoteExecuteService,
        private http: HttpService,
        private smartCacheServcie: SmartCacheService,
        public scheduleService: ScheduleService,
        private quotaService: UserQuotaService,
        private quotaSrt: UserQuotaStrService,
        private timeService: TimeService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        if (this.vstore && this.vstore.value) {
            this.tempVstoreValue = this.vstore.value;
        }

        this.hasWormLicense = this.permissionService.isLicenseExist([LicenseKey.HyperLock]);
        this.checkShow().subscribe(() => {
            // 审计日志入口默认关闭加入内置CDP计划
            if (this.entry === FileSystemEntryE.AuditLog) {
                this.cdpSwicth = false;
            }
            this.isCoverageStorHideDedupeAndCompression = FeatureConfig.IsCoverageStorWorkloadType && !FeatureConfig.IsSupportPelagodbQlcDataReductionDisplay;
            super.ngOnInit();
            this.supportCdpScheduleInit();
            this.init();
        }, () => this.close());
    }

    ngAfterViewInit(): void {
        if (this.cdpCardDiv?.nativeElement && this.cdpEmptyDiv?.nativeElement) {
            if (Number(this.cdpEmptyDiv.nativeElement.clientWidth) < Number(this.cdpCardDiv.nativeElement.clientWidth)) {
                if (this.entry === FileSystemEntryE.Main || this.data) {
                    this.cdpCardDiv.nativeElement.style.width = '7.76rem';
                } else {
                    this.cdpCardDiv.nativeElement.style.width = '6.95rem';
                }

                return;
            }
            this.cdpCardDiv.nativeElement.style.width = this.cdpEmptyDiv.nativeElement.clientWidth + 'px';
        }
    }

    setShowAntvirus(): void {
        // 没有展示防病毒的必要条件或者是选择所有租户，不展示防病毒
        if (!this.service.showAntvirusNecessaryCondition() ||
            !this.tempVstoreValue ||
            this.entry === FileSystemEntryE.AuditLog ||
            SWITCH.IS_SIMPLE_MODE) {
            this.showAntivirus = false;
            return;
        }

        this.service
            .getAntivirusServer(this.tempVstoreValue)
            .subscribe(({ data }: { data: AVServer[] }) => {
                // 对应租户开启防病毒服务器，才展示防病毒
                this.showAntivirus = Boolean(data.length);
            });
    }


    /**
     * 收集下发防病毒的公共参数
     *
     */
    collectAntivirusCommonParam(): {
        switch: boolean;
        fsName: string;
        vstoreId: string;
        vstoreName: string;
    } {
        return {
            switch: true,
            fsName: this.vm.value.name,
            vstoreId: this.tempVstoreValue,
            vstoreName: this.vstore && this.vstore.value ? this.vstore.label : this.vstoreSelect.selectedOption.label,
        };
    }

    checkShow(): Observable<string> {
        let vstore$ = of(null);
        if (this.vstore && this.vstore.value) {
            vstore$ = this.service.getVstore(this.vstore.value, { mask: true }).pipe(
                map(({ data }) => data),
            );
        }

        // 不能使用forkOrder，需要等到checkLicense后才能执行checkPermission
        let vstore: Vstore;
        return vstore$.pipe(
            map((value) => vstore = value),
            flatMap(() => this.checkPermission(vstore)),
        );
    }

    checkPermission(vstore?: Vstore): Observable<string> {
        let permissions;
        if (!vstore) {
            // 所有租户
            permissions = ['storage_pool_R'];
        } else if (!this.isHyperMetroVstore(vstore)) {
            // 选择特性租户， 租户是非双活
            permissions = ['storage_pool_R'];
        } else {
            permissions = ['task_W', 'hyper_metro_pair_W', 'hyper_metro_domain_R', 'storage_pool_R'];
        }

        return this.permissionService.checkDepPermission(permissions);
    }

    checkWormParam(): void {
        this.hasWormClockR = this.permissionService.hasPermission('global_secure_compliance_clock_R');
        this.hasWormClockW = this.permissionService.hasPermission('global_secure_compliance_clock_W');
    }

    onShow(): void {
        this.initVstoreAndPoolAndCapacity();
    }

    /**
     * 弹出license不存在弹窗
     */
    showNoLicensePropmt(licenseStr: string): Observable<void> {
        return Observable.create((observer: Subscriber<void>) => {
            const cancel = (): void => {
                this.promptService.close();
                observer.error();
                observer.complete();
            };

            this.promptService.prompt({
                title: this.i18nService.get('common.LicenseNotExist_title'),
                content: this.i18nService.get('common.licenseNotExistMsg', [licenseStr]),
                onOk: cancel,
                onCancel: cancel,
            });
        });
    }

    init(): void {
        // 二级存储需要先查询pool再根据pool version查询应用类型
        if (!FeatureConfig.IsQueryPoolBeforeQueryWorkloadTypesInFS) {
            this.getStoragePools();
            this.getWorkloadTypes();
        } else {
            if (this.data) {
                this.getWorkloadTypeAfterGetPool();
            } else {
                this.getStoragePools();
            }
        }
        // 新融合、二级存储、极简场景屏蔽smartcache
        if (FeatureConfig.IsSupportSmartCache && !SWITCH.IS_SIMPLE_MODE) {
            this.getSmartCachePartionList();
        }
        this.getDefaultAndOrgCdpSchedule();
        this.vstoreRest = this.service.getVstoreRest();

        this.supportGuaranteedCapacity = this.permissionService.isLicenseExist(
            [[LicenseKey.GuaranteedCapacity, LicenseKey.SpecialLicense]],
        );
        this.supportDedupeAndCompression = this.toolService.supportDedupeAndCompression();

        // 租户视图进入不再支持选租户，文本展示
        if (this.vstore && this.vstore.value) {
            this.selectedVstore = {
                ...this.vstore,
                data: {
                    ID: this.vstore.value,
                },
            };
            this.vm.get('vstoreItem').disable();

            this.getAuditLogEnabled(this.vstore.value);
        } else {
            this.vm.get('vstoreItem').enable();
        }

        if (this.data) {
            this.createAs()
                .subscribe(() => {
                    this.pageReady = true;
                    this.windowVisible = true;
                }, () => {
                    this.close();
                });
        } else {
            this.pageReady = true;
            this.windowVisible = true;
        }

        this.setShowAntvirus();

        // 订阅创建存储池任务执行完成
        this.taskSub$ = this.taskService.subscribe('storagePool', () => {
            this.getStoragePools();
        });
        this.updateRemotePoll();
    }

    initVm(): void {
        this.initVMData = { ...this.setBaseVmInitData() };
        // 判断智能推荐是否开启,若开启则表单初始化智能推荐数据,否则只初始化基本数据
        if (this.entry !== FileSystemEntryE.AuditLog && this.smartguiService.switch && !this.data && this.fsSmartguiData &&
            Object.keys(this.fsSmartguiData).length) {
            this.initVMData = {
                ...this.initVMData,
                ...this.getSmartguiVmInitData(),
            };
            this.advanced = true;
        }
        this.vm = new FormGroup({
            name: new FormControl(this.initVMData.name, {
                validators: [
                    Validators.required,
                    DMValidators.rangeByteLength(1, 255),
                    this.str.nameValidator,
                ],
            }),
            vstoreItem: new FormControl(null, {
                validators: [
                    Validators.required,
                    this.permissionCheck(),
                ],
            }),
            description: new FormControl('', {
                validators: DMValidators.rangeByteLength(0, 255),
            }),
            storagePool: new FormControl(null, {
                validators: [Validators.required],
            }),
            capacity: new FormControl({
                value: '',
                unit: Consts.GB,
            }, {
                validators: [
                    Validators.required,
                    DMValidators.capacityValidator(
                        true,
                        this.entry === FileSystemEntryE.AuditLog ? this.str.auditLogFsCapacityMin : this.str.fsCapacityMin,
                        this.str.fsCapacityMax,
                    ),
                ],
            }),
            remoteParentId: new FormControl(null, Validators.required),
            snapshotSpaceRatio: new FormControl(20),
            capacityThreshold: new FormControl(
                this.initVMData.capacityThreshold,
                this.capacityAndAutoGrowValidator(),
            ),
            workloadType: new FormControl('', [Validators.required]),
            snapshotReserveper: new FormControl(
                // 审计日志默认0
                this.entry === FileSystemEntryE.AuditLog ? 0 : this.initVMData.snapshotReserveper,
                [Validators.required],
            ),
            autodelSnapshotEnable: new FormControl(false, [Validators.required]),
            spaceselfAdjustingMode: new FormControl(CapacityPolicyE.DISABLED, [Validators.required]),
            autoGrowThresholdPercent: new FormControl(
                this.initVMData.autoGrowThresholdPercent,
                [
                    Validators.required,
                    this.autoPercentValidator(),
                    this.capacityAndAutoGrowValidator(),
                ],
            ),
            autoShrinkThresholdPercent: new FormControl(
                this.initVMData.autoShrinkThresholdPercent,
                [
                    Validators.required,
                    this.autoPercentValidator(),
                ],
            ),
            maxAutoSize: new FormControl(
                this.initAutoSizeDefaultValue(),
                {
                    validators: [
                        Validators.required,
                        this.maxAutoSizeAndCapacityValidator(),
                        this.autoSizeValidator(),
                    ],
                },
            ),
            minAutoSize: new FormControl(
                this.initAutoSizeDefaultValue(),
                {
                    validators: [
                        Validators.required,
                        this.minAutoSizeAndCapacityValidator(),
                        this.autoSizeValidator(),
                    ],
                },
            ),
            securityStyle: new FormControl(this.initVMData.securityStyle),
            nasLockingPolicy: new FormControl(this.initVMData.nasLockingPolicy),
            qosPolicy: new FormControl(null),
            isShowSnapshotDir: new FormControl(this.initVMData.isShowSnapshotDir),
            atime: new FormControl(this.initVMData.atime),
            atimeUpdateMode: new FormControl(this.initVMData.atimeUpdateMode),
            smartCachePartition: new FormControl(),
            addToCdpScheduleSwitch: new FormControl(this.cdpSwicth),
            selectedCdpSchedule: new FormControl(null, Validators.required),
            snapDiffSwitch: new FormControl(this.initVMData.snapDiffSwitch),
            auditItems: new FormControl([]),
        }, { updateOn: 'blur' });
        if (ProductConfig.isSupportVAAISwitch &&
            this.entry !== FileSystemEntryE.AuditLog) {
            this.vm.addControl('vaaiSwitch', new FormControl(this.initVMData.vaaiSwitch === VaaiSwitchE.ON, [Validators.required]));
        }
        this.vm.get('capacity').valueChanges.subscribe((newValue: Capacity) => {
            this.capLossTips = this.str.getLossTip(newValue);

            const oldValue = this.vm.value.capacity;

            if (newValue.value === oldValue?.value && newValue.unit === oldValue?.unit) {
                return;
            }

            this.vm.patchValue({ maxAutoSize: this.getAutoSizeDefaultValue('max') });
            this.vm.patchValue({ minAutoSize: this.getAutoSizeDefaultValue('min') });
        });

        this.vm.get('snapshotReserveper').valueChanges.subscribe((newValue: number) => {
            const oldValue = this.vm.value.snapshotReserveper;

            if (oldValue === newValue) {
                return;
            }

            const maxValue = this.getAutoSizeDefaultValue('max');
            this.vm.patchValue({ maxAutoSize: maxValue });
        });

        this.spaceselfAdjustingModeChange();

        const isAuditLogFS = this.entry === FileSystemEntryE.AuditLog;

        this.errorMessage = {
            name: {
                required: this.toolService.requiredErr(this.i18n.nameLab),
                name: this.i18n.nameFormatErr,
                rangeByteLength: this.toolService.rangeByteLengthErr(1, 255),
            },
            vstoreItem: {
                required: this.toolService.requiredErr(this.i18n.vStoreLab),
                permission: this.str.noPermissionToUser(
                    this.permissionService.getNoPermissions(['task_W', 'hyper_metro_pair_W', 'hyper_metro_domain_R']),
                ),
            },
            description: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 255),
            },
            storagePool: {
                required: this.toolService.requiredErr(this.i18n.storagePoolLab),
            },
            workloadType: {
                required: this.toolService.requiredErr(this.i18n.workloadTypeLab),
            },
            capacityThreshold: {
                capacityAndAutoGrow: this.str.i18n.capacityAndAutoExpandMsg,
            },
            autoGrowThresholdPercent: {
                autoPercent: this.str.i18n.autoPercentMsg,
                capacityAndAutoGrow: this.str.i18n.capacityAndAutoExpandMsg,
            },
            autoShrinkThresholdPercent: {
                autoPercent: this.str.i18n.autoPercentMsg,
            },
            capacity: {
                required: this.str.capacityRangeErr(
                    this.entry === FileSystemEntryE.AuditLog ?
                        this.str.auditLogFsCapacityMin :
                        this.str.fsCapacityMin, this.str.fsCapacityMax),
                min: this.str.capacityRangeErr(
                    this.entry === FileSystemEntryE.AuditLog ? this.str.auditLogFsCapacityMin : this.str.fsCapacityMin,
                    this.str.fsCapacityMax,
                ),
                max: this.str.capacityRangeErr(
                    this.entry === FileSystemEntryE.AuditLog ? this.str.auditLogFsCapacityMin : this.str.fsCapacityMin,
                    this.str.fsCapacityMax,
                ),
            },
            maxAutoSize: {
                required: this.str.autoSizeRangeErr('max', isAuditLogFS),
                autoSize: this.str.i18n.autoSizeMsg,
                defaultError: this.str.autoSizeRangeErr('max', isAuditLogFS),
                maxAutoSize: this.str.i18n.capacityGreateAutoExpandMsg,
            },
            minAutoSize: {
                required: this.str.autoSizeRangeErr('min', isAuditLogFS),
                autoSize: this.str.i18n.autoSizeMsg,
                defaultError: this.str.autoSizeRangeErr('min', isAuditLogFS),
                minAutoSize: this.str.minAutoSizeRangeErr(isAuditLogFS),
            },
            remoteParentId: { required: this.toolService.requiredErr(this.i18n.remoteStoragePollLab) },
            selectedCdpSchedule: {
                required: this.toolService.requiredErr(SWITCH.IS_SIMPLE_MODE ?
                    this.i18n.cdpScheduleTitle : this.i18n.hyperCdpScheduleForFileSystemLab),
            },
            FS: {
                required: this.toolService.requiredErr(this.i18n.fileSystem),
            },
        };
    }

    /**
     * 容量自适应策略改变，容量自适应策略修改为‘不适用’时，相关5个字段隐藏，容量自适应策略修改为‘自动扩容’时，缩容字段隐藏
     */
    spaceselfAdjustingModeChange(): void {
        this.vm.get('spaceselfAdjustingMode').valueChanges.subscribe((modeValue: string) => {
            const capacityThresholdControl = this.vm.get('capacityThreshold') as FormControl;
            const autoGrowThresholdPercentControl = this.vm.get('autoGrowThresholdPercent') as FormControl;
            const autoShrinkThresholdPercentControl = this.vm.get('autoShrinkThresholdPercent') as FormControl;
            const maxAutoSizeControl = this.vm.get('maxAutoSize') as FormControl;
            const minAutoSizeControl = this.vm.get('minAutoSize') as FormControl;

            if (modeValue === CapacityPolicyE.DISABLED) {
                autoGrowThresholdPercentControl.disable();
                autoShrinkThresholdPercentControl.disable();
                maxAutoSizeControl.disable();
                minAutoSizeControl.disable();
                // 状态disable后手动触发一下告警阈值的校验
                capacityThresholdControl.clearError('capacityAndAutoGrow');
            } else if (modeValue === CapacityPolicyE.AUTOEXPANSION) {
                autoGrowThresholdPercentControl.enable();
                autoGrowThresholdPercentControl.markAsTouched();
                autoGrowThresholdPercentControl.clearError('autoPercent');
                autoShrinkThresholdPercentControl.disable();
                maxAutoSizeControl.enable();
                maxAutoSizeControl.clearError('autoSize');
                minAutoSizeControl.disable();
            } else if (modeValue === CapacityPolicyE.AUTOEXPANSIONANDREDUCTION) {
                autoGrowThresholdPercentControl.enable();
                autoShrinkThresholdPercentControl.enable();
                maxAutoSizeControl.enable();
                minAutoSizeControl.enable();
                // 状态enable后手动触发一下校验
                autoGrowThresholdPercentControl.markAsTouched();
                autoShrinkThresholdPercentControl.markAsTouched();
                maxAutoSizeControl.markAsTouched();
                minAutoSizeControl.markAsTouched();
            } else {
                // 其他策略
            }
        });
    }

    initAutoSizeDefaultValue(): { value: number; unit: string } {
        const isAuditLog = this.entry === FileSystemEntryE.AuditLog;
        return {
            value: isAuditLog ? this.auditLogCapacity : this.maxCapacity,
            unit: isAuditLog ? Consts.GB : Consts.PB,
        };
    }

    /**
     * 告警阈值必须大于扩容触发门限
     */
    capacityAndAutoGrowValidator() {
        return (control: AbstractControl): ValidationErrors | null => {
            // 不使用容量自适应时，不需要校验
            const rootCtrl = control.root;
            const modeContrl = rootCtrl.get('spaceselfAdjustingMode') as FormControl;

            if (!modeContrl || modeContrl.value === CapacityPolicyE.DISABLED) {
                return null;
            }

            const capacityThresholdContrl = rootCtrl.get('capacityThreshold') as FormControl;
            const maxPercentContrl = rootCtrl.get('autoGrowThresholdPercent') as FormControl;

            if (!capacityThresholdContrl || !maxPercentContrl) {
                return null;
            }

            if (capacityThresholdContrl.value > maxPercentContrl.value) {
                capacityThresholdContrl.clearError('capacityAndAutoGrow');
                maxPercentContrl.clearError('capacityAndAutoGrow');
                return null;
            }

            return { capacityAndAutoGrow: true };
        };
    }

    autoPercentValidator() {
        return (control: AbstractControl): ValidationErrors | null => {
            const rootCtrl = control.root;
            // 自动扩容策略为‘不适用’时，不做校验
            const modeContrl = rootCtrl.get('spaceselfAdjustingMode') as FormControl;

            if (!modeContrl || modeContrl.value === CapacityPolicyE.DISABLED) {
                return null;
            }

            const maxPercent = rootCtrl.get('autoGrowThresholdPercent') as FormControl;
            const minPercent = rootCtrl.get('autoShrinkThresholdPercent') as FormControl;

            if (!maxPercent || !minPercent || minPercent.disabled) {
                return null;
            }

            if (maxPercent.value > minPercent.value) {
                maxPercent.clearError('autoPercent');
                minPercent.clearError('autoPercent');
                return null;
            }

            return { autoPercent: true };
        };
    }

    autoSizeValidator() {
        return (control: AbstractControl): ValidationErrors | null => {
            const rootCtrl = control.root;
            // 自动扩容策略为‘不适用’时，不做校验
            const modeContrl = rootCtrl.get('spaceselfAdjustingMode') as FormControl;

            if (!modeContrl || modeContrl.value === CapacityPolicyE.DISABLED) {
                return null;
            }

            const maxAutoSize = rootCtrl.get('maxAutoSize') as FormControl;
            const minAutoSize = rootCtrl.get('minAutoSize') as FormControl;

            if (!maxAutoSize || !minAutoSize || minAutoSize.disabled) {
                return null;
            }

            const maxContrlVal = maxAutoSize.value;
            const maxAdaptVal = this.utilService.adaptUnit(maxContrlVal.value, maxContrlVal.unit, Consts.BYTE);
            const minContrlVal = minAutoSize.value;
            const minAdaptVal = this.utilService.adaptUnit(minContrlVal.value, minContrlVal.unit, Consts.BYTE);

            if (maxAdaptVal.value >= minAdaptVal.value) {
                maxAutoSize.clearError('autoSize');
                minAutoSize.clearError('autoSize');
                return null;
            }

            return { autoSize: true };
        };
    }

    /**
     * 1、自动扩容上限大于等于文件系统容量
     * 2、自动扩容上限小于等于32PB
     */
    maxAutoSizeAndCapacityValidator() {
        return (control: AbstractControl): ValidationErrors | null => {
            const rootCtrl = control.root;
            // 自动扩容策略为‘不适用’时，不做校验
            const modeContrl = rootCtrl.get('spaceselfAdjustingMode') as FormControl;

            if (!modeContrl || modeContrl.value === CapacityPolicyE.DISABLED) {
                return null;
            }

            const maxAutoSize = rootCtrl.get('maxAutoSize') as FormControl;
            const capacity = rootCtrl.get('capacity') as FormControl;

            if (!maxAutoSize) {
                return null;
            }

            const maxContrlVal = maxAutoSize.value;
            const maxAdaptVal = this.utilService.adaptUnit(maxContrlVal.value, maxContrlVal.unit, Consts.BYTE);
            const capacityVal = capacity.value;

            if (!capacityVal.value) {
                const minLimitVal = this.utilService.adaptUnit(this.minCapacity, Consts.GB, Consts.BYTE);
                const maxLImitVal = this.utilService.adaptUnit(this.maxCapacity, Consts.PB, Consts.BYTE);
                if (minLimitVal.value <= maxAdaptVal.value && maxAdaptVal.value <= maxLImitVal.value) {
                    maxAutoSize.clearError('defaultError');
                    return null;
                } else {
                    return { defaultError: true };
                }
            }

            const capacityAdaptVal = this.utilService.adaptUnit(capacityVal.value, capacityVal.unit, Consts.BYTE);
            const maxLimitVal = this.utilService.adaptUnit(this.maxCapacity, Consts.PB, Consts.BYTE);

            if (capacityAdaptVal.value <= maxAdaptVal.value && maxAdaptVal.value <= maxLimitVal.value) {
                maxAutoSize.clearError('maxAutoSize');
                return null;
            }
            return { maxAutoSize: true };
        };
    }

    /**
     * 1、自动缩容下限小于等于文件系统容量
     * 2、自动缩容下限大于等于1GB，如果审计日志文件系统下限大于等于5GB
     * 3、未配置容量时，使用下限1GB，上限32PB进行范围校验
     */
    minAutoSizeAndCapacityValidator() {
        return (control: AbstractControl): ValidationErrors | null => {
            const rootCtrl = control.root;
            // 自动扩容策略为‘不适用’时，不做校验
            const modeContrl = rootCtrl.get('spaceselfAdjustingMode') as FormControl;

            if (!modeContrl || modeContrl.value === CapacityPolicyE.DISABLED) {
                return null;
            }

            const minAutoSize = rootCtrl.get('minAutoSize') as FormControl;
            const capacity = rootCtrl.get('capacity') as FormControl;

            if (!minAutoSize) {
                return null;
            }

            const minContrlVal = minAutoSize.value;
            const minAdaptVal = this.utilService.adaptUnit(minContrlVal.value, minContrlVal.unit, Consts.BYTE);
            const capacityVal = capacity.value;
            let minLimitVal = null;

            if (!capacityVal.value) {
                const min = this.entry === FileSystemEntryE.AuditLog ? this.auditLogCapacity : this.minCapacity;
                minLimitVal = this.utilService.adaptUnit(min, Consts.GB, Consts.BYTE);
                const maxLImitVal = this.utilService.adaptUnit(this.maxCapacity, Consts.PB, Consts.BYTE);

                if (minLimitVal.value <= minAdaptVal.value && minAdaptVal.value <= maxLImitVal.value) {
                    minAutoSize.clearError('defaultError');
                    return null;
                } else {
                    return { defaultError: true };
                }
            }

            const capacityAdaptVal = this.utilService.adaptUnit(capacityVal.value, capacityVal.unit, Consts.BYTE);
            minLimitVal = this.entry === FileSystemEntryE.AuditLog ?
                this.utilService.adaptUnit(this.auditLogCapacity, Consts.GB, Consts.BYTE) :
                this.utilService.adaptUnit(this.minCapacity, Consts.GB, Consts.BYTE);

            if (minAdaptVal.value <= capacityAdaptVal.value && minAdaptVal.value >= minLimitVal.value) {
                minAutoSize.clearError('minAutoSize');
                return null;
            }
            return { minAutoSize: true };
        };
    }

    /**
     * 获取自动扩容上限或者自动缩容下限
     * 1、扩容上限 = 容量 * （1 + 空间预留比）
     * 2、缩容下限 = 容量
     * 3、计算结果 < 1GB时，默认为1GB
     * 4、计算结果 > 32PB时，默认为32PB
     * 5、未配置容量时，上限和下限都默认为32PB
     */
    getAutoSizeDefaultValue(type: 'max' | 'min'): Capacity {
        if (!this.vm) {
            return { value: this.maxCapacity, unit: Consts.PB };
        }

        const capacityValue = this.vm.get('capacity').value;
        const capacity = capacityValue.value;
        const unit = capacityValue.unit;
        const capacityAdaptValue = Number(this.utilService.adaptUnit(capacity, unit, Consts.SECTOR).value);
        const minValue = Number(this.utilService.adaptUnit(this.minCapacity, Consts.GB, Consts.SECTOR).value);
        const maxValue = Number(this.utilService.adaptUnit(this.maxCapacity, Consts.PB, Consts.SECTOR).value);

        if (type === 'max') {
            const snapshotReserveperValue = Number(this.vm.get('snapshotReserveper').value);
            // 自动扩容上限 =当前的配置容量*（1+快照空间预留比）
            const maxAdaptValue = Number((capacityAdaptValue * (1 + snapshotReserveperValue / 100)).toFixed(3));

            if (maxAdaptValue < minValue) {
                return { value: this.minCapacity, unit: Consts.GB };
            } else if (maxAdaptValue > maxValue) {
                return { value: this.maxCapacity, unit: Consts.PB };
            } else {
                let maxDefaultValue = Number(this.utilService.adaptUnit(maxAdaptValue, Consts.SECTOR, unit).value);

                // 单位是不可输入小数的单位时，向下取整
                if (this.notDotUnits.includes(unit)) {
                    maxDefaultValue = Math.floor(maxDefaultValue);
                }

                return { value: maxDefaultValue, unit };
            }
        } else {
            if (capacityAdaptValue < minValue) {
                return { value: this.minCapacity, unit: Consts.GB };
            } else if (capacityAdaptValue > maxValue) {
                return { value: this.maxCapacity, unit: Consts.PB };
            } else {
                return capacityValue;
            }
        }
    }

    /**
     * 创建表单基本初始化数据
     **/
    setBaseVmInitData(): CreateFsVmInitItem {
        return {
            name: '',
            capacityThreshold: 90,
            securityStyle: '3',
            nasLockingPolicy: NasLockingPolicyModeE.Mandatory,
            isShowSnapshotDir: true,
            atime: false,
            atimeUpdateMode: 3600,
            snapshotReserveper: 20,
            autoGrowThresholdPercent: 85,
            autoShrinkThresholdPercent: 50,
            snapDiffSwitch: false,
        };
    }

    /**
     * smartgui开启时，表单初始化数据
     */
    getSmartguiVmInitData(): CreateFsVmInitItem {
        enum AtimeUpdateType {
            UpdateDisabled = 4294967295,
            UpdateEveryHour = 3600,
            UpdateEveryDay = 86400
        }
        const guiData: CreateFsVmInitItem = {
            name: this.fsSmartguiData.NAME[0],
            capacityThreshold: Number(this.fsSmartguiData.capacitythreshold),
            securityStyle: this.fsSmartguiData.securitystyle,
            nasLockingPolicy: this.fsSmartguiData.naslockingpolicy || NasLockingPolicyModeE.Mandatory,
            snapshotReserveper: this.fsSmartguiData.snapshotreserveper,
            isShowSnapshotDir: this.fsSmartguiData.isshowsnapdir === 'true',
            atime: this.fsSmartguiData.atime === 'true',
            atimeUpdateMode: Number(this.fsSmartguiData.atimeupdatemode) === AtimeUpdateType.UpdateDisabled ?
                AtimeUpdateType.UpdateEveryHour :
                Number(this.fsSmartguiData.atimeupdatemode),
        };
        if (ProductConfig.isSupportVAAISwitch &&
            this.entry !== FileSystemEntryE.AuditLog) {
            guiData.vaaiSwitch = this.fsSmartguiData.vaaiswitch;
        }
        return guiData;
    }

    initVstoreAndPoolAndCapacity(): void {
        // 预设存储池和租户、容量表单项
        if (this.entry !== FileSystemEntryE.AuditLog && this.smartguiService.switch && !this.data && this.fsSmartguiData &&
            Object.keys(this.fsSmartguiData).length) {
            setTimeout(() => {
                this.setVstoreAndPoolDefault();
                this.vm.get('capacity').setValue(this.getCapObj(Number(this.fsSmartguiData.capacity)));
            }, 0);
        }
    }

    /**
     * 设置租户和存储池表单项默认选中智能推荐数据
     */
    setVstoreAndPoolDefault(): void {
        if (this.fsSmartguiData && this.fsSmartguiData.selectAllVstore) {
            this.vstoreSelect.setFixedOption({
                value: this.fsSmartguiData.vstoreItem.value,
                label: this.fsSmartguiData.vstoreItem.label,
                data: {
                    ID: this.fsSmartguiData.vstoreItem.value,
                    NAME: this.fsSmartguiData.vstoreItem.label,
                },
            });
        }
        if (this.fsSmartguiData && this.fsSmartguiData.storagePoolItem) {
            this.poolSelect.setFixedOption({
                value: this.fsSmartguiData.storagePoolItem.ID,
                label: this.fsSmartguiData.storagePoolItem.NAME,
                data: {
                    ID: this.fsSmartguiData.storagePoolItem.ID,
                    NAME: this.fsSmartguiData.storagePoolItem.NAME,
                },
            });
        }
    }

    /**
     * smartgui容量转换
     */
    getCapObj(capacity: number): {
        value: number;
        unit: string;
    } {
        const capObj = this.service.getValueAndUnit(capacity);
        return {
            value: capObj.value,
            unit: capObj.unit,
        };
    }

    /**
     * 根据存储池查询应用类型
     */
    getWorkloadTypeAfterGetPool(): void {
        this.storagePoolService.getStoragePoolOpts()
            .subscribe((options) => {
                this.storagePoolOpts = options;

                const pool = options.find((item) => item.value === this.data.PARENTID).data;
                this.selectedStoragePool = pool;

                this.getWorkloadTypes(pool.version);
            });
    }

    /**
     * 查询所属存储池选项
     */
    getStoragePools(): void {
        this.showNoPoolMessageTip = false;
        // 有指定的租户才查存储池
        let vstoreId: string;
        if (this.selectedVstore && this.selectedVstore.value) {
            vstoreId = this.selectedVstore.value;
        } else if (this.vstore && this.vstore.value) {
            vstoreId = this.vstore.value;
        } else {
            return;
        }

        // 在重新查询前置空远端存储池，重新查询后会按规则赋值
        this.resetPoolControlValue();
        this.storagePoolService.getStoragePoolOpts(vstoreId)
            .subscribe((options) => {
                this.showNoPoolMessageTip = true;
                this.storagePoolOpts = options;

                // 极简模式下多个存储池时，默认选中第一个
                if (SWITCH.IS_SIMPLE_MODE && this.storagePoolOpts.length > 0) {
                    const selectedDefaultPool = this.storagePoolOpts[0].value;
                    if (!selectedDefaultPool) {
                        return;
                    }
                    this.vm.patchValue({
                        storagePool: selectedDefaultPool,
                    });
                    return;
                }
                // 只有一项时默认选中
                if (this.storagePoolOpts.length === 1) {
                    this.vm.patchValue({
                        storagePool: this.storagePoolOpts[0].value,
                    });
                    // 自动选中后，触发一下存储池change函数，在change函数中判断是否二级存储从而判断是否查询应用类型
                    this.onStoragePoolChange({ option: this.storagePoolOpts[0] });
                }
            });
    }

    /**
     * 获取应用类型
     */
    getWorkloadTypes(version?: PoolVersionE): Subscription {
        return this.service.getWorkloadTypes(version)
            .pipe(
                finalize(() => {
                    if (this.loading) {
                        this.loading = false;
                    }
                }),
            )
            .subscribe(({ data }) => {
                this.workloadTypeOpts = ((data || []) as WorkloadType[]).map((a) => ({
                    label: this.utilService.decodeHTML(a.NAME),
                    value: a.ID,
                    data: a,
                }));
                let defaultWorkloadTypeId: string | number;
                const isOldPoolVersion = PoolOldVersions.some((item) => item === version);

                if (FeatureConfig.IsShowDefaultWorkloadTypeForOceanProtect) {
                    // 复制模式采用已有应用类型
                    if (this.data && !Object.prototype.hasOwnProperty.call(this.vm.changedValue, 'workloadType')) {
                        defaultWorkloadTypeId = this.data.workloadTypeId;
                    } else {
                        // 应用类型采用默认id
                        defaultWorkloadTypeId = DefaultFsWorkloadTypeE.Reduction_Global_Mode;

                        if (isOldPoolVersion) {
                            //VDP全局重删需求设置新增应用类型为默认值
                            defaultWorkloadTypeId = DefaultFsWorkloadTypeE.Old_Nas_Backup_Default;
                        }
                    }
                } else {
                    // 复制模式采用已有应用类型
                    if (this.data) {
                        defaultWorkloadTypeId = this.data.workloadTypeId;
                    } else if (this.fsSmartguiData) {
                        // 应用类型项采用智能推荐的id
                        defaultWorkloadTypeId = this.fsSmartguiData.workloadtypeid;
                    } else {
                        // 应用类型采用默认id
                        defaultWorkloadTypeId = DefaultFsWorkloadTypeE.NAS_default;
                    }
                }

                let defaultWorkloadType = this.workloadTypeOpts.find((a) => a.value === defaultWorkloadTypeId);

                if (!defaultWorkloadType && this.workloadTypeOpts.length === 1) {
                    defaultWorkloadType = this.workloadTypeOpts[0];
                }

                // 设置默认应用类型选中
                if (defaultWorkloadType) {
                    this.vm.patchValue({
                        workloadType: defaultWorkloadType.value,
                    });
                    this.onWorkloadTypeChange({ option: defaultWorkloadType });
                }
            });
    }

    /**
     * CDP计划状态渲染
     */
    statusRender = (status: string): string => {
        const item = this.str.scheduleStatusOpts.find((a) => a.value === status);
        return item ? item.label : UNPLACEHOLDER;
    };

    /**
     * 存储池切换
     */
    onStoragePoolChange({ option }): void {
        this.selectedStoragePool = option?.data;

        // 二级存储需要根据version查询workloadType
        if (FeatureConfig.IsQueryPoolBeforeQueryWorkloadTypesInFS) {
            this.loading = true;
            // 清空所选应用类型
            this.selectedWorkloadType = null;
            this.getWorkloadTypes(option.data.version);
        }
    }

    /**
     * 应用类型切换
     * @param param0
     */
    onWorkloadTypeChange({ option }): void {
        this.selectedWorkloadType = option.data;

        this.showVariableSegment = FeatureConfig.IsShowVariableSegmentInFSWorkloadType && this.selectedWorkloadType.variableSegment !== '0';
    }

    onVstoreChange($event?): void {
        this.tempVstoreValue = $event.option.value;
        this.selectedVstore = $event.option;
        this.cdpScheduleRest = this.scheduleService.getBatchRest(ScheduleTypeE.File, $event.option.value);

        // 1、smartGui入口第一次vstoreChange触发比较靠前，导致defaultCdpSchedule还没查上来。这种场景确保defaultCdpSchedule查到才赋值
        // 2、非smartGui场景，用户切租户时再赋值
        if (!this.fsSmartguiData || this.defaultCdpSchedule) {
            this.defaultOption = {
                label: DEFAULT_CDP_SCHEDULE_NAME,
                value: DEFAULT_CDP_SCHEDULE_NAME,
                data: this.defaultCdpSchedule,
            };
        }
        this.updateRemotePoll();
        // 租户更新后重新查存储池
        this.getStoragePools();
        this.getAuditLogEnabled($event.option.value);

        this.setShowAntvirus();

        this.getAntivirusPolicy();
    }

    getAntivirusPolicy(): void {
        if (this.ondemandCard) {
            this.ondemandCard.handldeUsedInFs();
        }

        if (this.onaccessCard) {
            this.onaccessCard.handldeUsedInFs();
        }
    }

    cdpSelectChange(option: SelectOption): void {
        // 策略渲染
        if (option && option.data) {
            option.data.strategy = this.scheduleService.renderStrategy(option.data);
        }
    }

    cdpScheduleSwitchChange($event: boolean): void {
        if ($event) {
            this.vm.get('selectedCdpSchedule').enable();
        } else {
            this.vm.get('selectedCdpSchedule').disable();
        }
    }

    updateRemotePoll(): void {
        // 极简场景屏蔽远端存储池表单
        if (SWITCH.IS_SIMPLE_MODE) {
            this.vm.get('remoteParentId').disable();
            this.remoteStoragePoolOpts = [];
            return;
        }
        if (!this.selectedVstore) {
            this.wormConfig?.updateWormShow(false);
            return;
        }
        this.service.getVstorePair(this.selectedVstore.data.ID).pipe(
            flatMap(({ data }) => {
                if (data && data.length) {
                    this.vstorePairId = data[0].ID;
                    this.remoteVstoreName = data[0].REMOTEVSTORENAME;
                    this.remoteVstoreId = data[0].REMOTEVSTOREID;
                    this.wormConfig?.updateWormShow(this.vstorePairId !== undefined);

                    return this.service.getFsHyperMetroDomain(data[0].DOMAINID);
                }
                this.wormConfig?.updateWormShow(false);
                this.vstorePairId = null;
                throw new Error('no pair');
            }),
            flatMap(({ data }) => {
                this.fsHyperMetroDomain = data;
                return this.remoteExecuteService.get(JSON.parse(data.REMOTEDEVICES)[0].devId,
                    this.http.makeUrl('storagePool.getBatch').setParams({ vstoreId: this.remoteVstoreId }),
                    { mask: true },
                );
            }),
            map(({ data }) => {
                if (data && data.length) {
                    return data.map((item) => ({
                        value: item.ID,
                        label: item.NAME,
                    }));
                }
                return [];
            }),
        ).subscribe((data: SelectOption[]) => {
            if (this.entry === FileSystemEntryE.AuditLog) {
                this.vm.get('remoteParentId').disable();
                this.remoteStoragePoolOpts = [];
            } else if (this.vstorePairId) {
                this.vm.get('remoteParentId').enable();
                this.remoteStoragePoolOpts = data;
                this.vm.patchValue({ remoteParentId: this.remoteParentId || null });
            } else {
                this.vm.get('remoteParentId').disable();
                this.remoteStoragePoolOpts = [];
            }
        }, () => {
            this.vm.get('remoteParentId').disable();
            this.remoteStoragePoolOpts = [];
        });
    }

    getSmartCachePartionList(): void {
        this.smartCacheServcie.getSmartCachePool()
            .pipe(
                filter((res) => Boolean(res.data.length)),
                switchMap((res) => {
                    const params = {
                        associateObjType: 218,
                        associateObjID: res.data[0].id,
                    };
                    return this.service.getAssociatePartition(params).pipe(filter((item) => Boolean(item.data.length)));
                }),
            )
            .subscribe((res) => {
                const partitionList: SelectOption[] = [];
                res.data.forEach((partition) => {
                    partitionList.push(
                        {
                            label: partition.name,
                            value: partition.id,
                        },
                    );
                });
                this.smartCachePartionList = partitionList;
                let defaulSmartCachePartionId: string | number;
                // 复制模式采用已有分区
                if (this.data) {
                    defaulSmartCachePartionId = this.data.SMARTCACHEPARTITIONID;
                }
                // 待优化，当分区规格较大时，该方法可能会返回空
                const defaulSmartCachePartion = this.smartCachePartionList.find((a) => a.value === defaulSmartCachePartionId);
                // 设置分区选中
                if (defaulSmartCachePartion) {
                    this.vm.patchValue({
                        smartCachePartition: defaulSmartCachePartion.value,
                    });
                    this.onSmartCachePartitionChange({ option: defaulSmartCachePartion });
                }
            });
    }

    /**
     * 获取默认CDP定时计划和模板创建入口源文件系统CDP计划
     */
    getDefaultAndOrgCdpSchedule(): Promise<unknown> {
        if (!this.hasCdpReadPermission) {
            return Promise.resolve();
        }
        const cmds = [
            this.scheduleService.getDefaultCdpSchedule(),
        ];

        if (this.data) {
            cmds.push(this.scheduleService.getOrgCdpSchedule(this.data.vstoreId, this.data.TIMINGSNAPSHOTSCHEDULEID));
        }

        return new Promise((resolve) => {
            forkJoin(cmds).subscribe((results: any[]) => {
                const defaultCdpSchedule = results[0]?.data;
                const orgCdpSchedule = results[1]?.data && results[1]?.data[0];

                this.defaultCdpSchedule = defaultCdpSchedule.find((item: HyperCdpSchedule) => item.isBuildinSchedule === 'true');

                // 构造内置CDP计划的策略
                if (this.defaultCdpSchedule) {
                    this.defaultCdpSchedule.strategy = this.scheduleService.renderStrategy(this.defaultCdpSchedule);
                }

                // 模板创建需要赋默认值
                if (this.cdpScheduleSelect && this.data) {
                    this.cdpScheduleRest = this.scheduleService.getBatchRest(ScheduleTypeE.File, this.data.vstoreId);

                    // 底层查不出内置CDP计划，需要先构造一个
                    if (!this.defaultCdpSchedule) {
                        this.defaultOption = {
                            label: DEFAULT_CDP_SCHEDULE_NAME,
                            value: DEFAULT_CDP_SCHEDULE_NAME,
                            data: this.defaultCdpSchedule,
                        };
                    }

                    this.cdpScheduleSelect.select({
                        label: orgCdpSchedule ? orgCdpSchedule.NAME : DEFAULT_CDP_SCHEDULE_NAME,
                        value: orgCdpSchedule ? orgCdpSchedule.NAME : DEFAULT_CDP_SCHEDULE_NAME,
                        data: orgCdpSchedule || this.defaultCdpSchedule,
                    });
                    this.cdpScheduleSelect.onTouchedInterface();
                } else if (this.fsSmartguiData || this.vstore?.value) {
                    // smartGui入口用推荐的租户，否则用传入的租户
                    const vstoreId = this.fsSmartguiData?.vstoreItem.value || this.vstore?.value;

                    // smartGui入口和传入了vstore的情况需要赋默认值
                    this.cdpScheduleRest = this.scheduleService.getBatchRest(ScheduleTypeE.File, vstoreId);
                    this.defaultOption = {
                        label: DEFAULT_CDP_SCHEDULE_NAME,
                        value: DEFAULT_CDP_SCHEDULE_NAME,
                        data: this.defaultCdpSchedule,
                    };
                } else {
                    // 其他场景
                }

                resolve(true);
            });
        });
    }

    /**
     * 获取审计日志开关是否开启
     */
    getAuditLogEnabled(vstoreId: string): void {
        if (!this.hasAuditStrategyW || SWITCH.IS_SIMPLE_MODE) {
            return;
        }
        this.service.getAuditLogEnabled(vstoreId).subscribe(
            (status) => {
                this.isAuditLogEnabled = status;
            },
            () => {
                this.isAuditLogEnabled = false;
            },
        );
    }

    onSmartCachePartitionChange({ option }): void {
        this.selectedSCPName = option.label;
    }

    /**
     * 按业务要求：
     * 1、扩容门限值为0，赋默认值：85
     * 2、缩容门限值为0，赋默认值：50
     * 3、扩容上限值为0，附默认值：容量 * (1 + 快照空间比)
     * 4、缩容下限值为0，赋默认值：容量
     */
    getDefValue(): {
        autoGrowDefValue: string | number;
        autoShrinkDefValue: string | number;
        maxAutoSizeDefValue: string;
        minAutoSizeDefValue: string;
    } {
        const data = this.data;
        const autoGrowDefValue = Number(data.AUTOGROWTHRESHOLDPERCENT) ?
            data.AUTOGROWTHRESHOLDPERCENT : this.str.autoGrowDefValue;
        const autoShrinkDefValue = Number(data.AUTOSHRINKTHRESHOLDPERCENT) ?
            data.AUTOSHRINKTHRESHOLDPERCENT : this.str.autoShrinkDefValue;
        const maxAutoSizeDefValue = Number(data.MAXAUTOSIZE) ? data.MAXAUTOSIZE : this.getMaxAutoDefVal();
        const minAutoSizeDefValue = Number(data.MINAUTOSIZE) ? data.MINAUTOSIZE : data.CAPACITY;
        return {
            autoGrowDefValue,
            autoShrinkDefValue,
            maxAutoSizeDefValue,
            minAutoSizeDefValue,
        };
    }

    /**
     * 获取扩容上限默认值
     */
    getMaxAutoDefVal(): string {
        const capacityAdaptValue = Number(this.data.CAPACITY);
        const snapshotReserveperValue = Number(this.data.SNAPSHOTRESERVEPER);
        const maxAdaptValue = (capacityAdaptValue * (1 + (snapshotReserveperValue / 100))).toFixed(3);
        return maxAdaptValue;
    }

    /**
     * 处理create as
     */
    createAs(): Observable<any> {
        const data = this.data;
        const capacity = this.utilService.adaptUnit(data.CAPACITY, CAPACITY_UNIT.SECTOR);

        const defValue = this.getDefValue();
        const maxAutoSize = this.utilService.adaptUnit(defValue.maxAutoSizeDefValue, CAPACITY_UNIT.SECTOR);
        const minAutoSize = this.utilService.adaptUnit(defValue.minAutoSizeDefValue, CAPACITY_UNIT.SECTOR);
        // 模板创建时，如果是worm文件系统，默认开启worm设置
        this.wormSwitch = this.data.SUBTYPE === FileSystemModeE.WORM;
        if (this.wormSwitch) {
            this.wormSwitchChange(true);
            // 模板创建时‘WORM审计日志文件系统’字段，默认关闭
            this.data.isWormAuditLogFs = '0';
        }

        this.vm.patchValue({
            description: this.utilService.decodeHTML(data.DESCRIPTION),
            storagePool: data.PARENTID,
            capacity: {
                value: capacity.value,
                unit: capacity.unit,
            },
            snapshotSpaceRatio: Number(data.SNAPSHOTRESERVEPER),
            capacityThreshold: Number(data.CAPACITYTHRESHOLD),
            securityStyle: data.securityStyle,
            nasLockingPolicy: this.service.isValidValue(String(data.nasLockingPolicy)) ?
                data.nasLockingPolicy :
                NasLockingPolicyModeE.Mandatory,
            isShowSnapshotDir: data.ISSHOWSNAPDIR === 'true',
            atime: data.ATIME === 'true',
            snapDiffSwitch: data.snapDiffSwitch,
            atimeUpdateMode: data.ATIME === 'true' ? Number(data.ATIMEUPDATEMODE) : 3600,
            auditItems: this.str.getSelectedAuditItem(Number(this.data.auditItems)),
            snapshotReserveper: Number(data.SNAPSHOTRESERVEPER),
            autodelSnapshotEnable: data.AUTODELSNAPSHOTENABLE === 'true',
            spaceselfAdjustingMode: data.SPACESELFADJUSTINGMODE,
            autoGrowThresholdPercent: Number(defValue.autoGrowDefValue),
            autoShrinkThresholdPercent: Number(defValue.autoShrinkDefValue),
            maxAutoSize: {
                value: maxAutoSize.value,
                unit: maxAutoSize.unit,
            },
            minAutoSize: {
                value: minAutoSize.value,
                unit: minAutoSize.unit,
            },
        });
        if (ProductConfig.isSupportVAAISwitch &&
            this.entry !== FileSystemEntryE.AuditLog) {
            this.vm.patchValue({ vaaiSwitch: data.vaaiSwitch === VaaiSwitchE.ON });
        }

        return of(null);
    }

    /**
     * 检查是否走omtask的接口
     */
    checkIfCreateByOmTask(): boolean {
        // 双活
        const isHyperMetro = Boolean(this.vstorePairId) && this.entry !== FileSystemEntryE.AuditLog;

        // 有共享或者双活或者防病毒服务，走omtask
        if (this.vm.value.NFScreateShare ||
            this.vm.value.CIFScreateShare ||
            isHyperMetro ||
            (this.tempVstoreValue === '0' && this.vm.value.DataTurbocreateShare) ||
            this.hasUsedAntivirusService()) {
            return true;
        }

        return false;
    }

    /**
     * 是否开启防病毒服务
     *
     * @returns boolean
     */
    hasUsedAntivirusService(): boolean {
        return (this.onaccessCard && this.onaccessCard.switch) || (this.ondemandCard && this.ondemandCard.switch);
    }

    /**
     * 构建下发命令
     */
    buildCmds(): Command[] {
        const cmds: Command[] = this.buildCreateFileSystemCmd();

        return cmds;
    }

    getAdaptValue(capacity: string, unit: string): string {
        return String(Math.floor(this.utilService.adaptUnit(capacity, unit, 'Sector').value));
    }

    /**
     * 构造基础项参数
     */
    buildBasicParams(): FileSystem {
        const vmValue = this.vm.value;
        const params: FileSystem = {};

        if (vmValue.capacity.value) {
            params.CAPACITY =
                String(Math.floor(this.utilService.adaptUnit(vmValue.capacity.value, vmValue.capacity.unit, 'Sector').value));
        }

        if (ProductConfig.isSupportVAAISwitch && this.entry !== FileSystemEntryE.AuditLog) {
            params.vaaiSwitch = vmValue.vaaiSwitch ? VaaiSwitchE.ON : VaaiSwitchE.OFF;
        }

        if (this.unixPermissionConfig && vmValue.securityStyle !== SecurityModeE.NTFS) {
            params.unixPermissions = this.unixPermissionConfig.getVmValue();
        }

        return params;
    }

    /**
     * 构建容量自适应相关参数
     * @returns
     */
    buildSpaceselfAdjustingParams(): FileSystem {
        const vmValue = this.vm.value;
        const params: FileSystem = {};
        // 不使用容量自适应功能时，不下发相关参数
        if (vmValue.spaceselfAdjustingMode === CapacityPolicyE.AUTOEXPANSIONANDREDUCTION) {
            params.AUTOGROWTHRESHOLDPERCENT = vmValue.autoGrowThresholdPercent;
            params.AUTOSHRINKTHRESHOLDPERCENT = vmValue.autoShrinkThresholdPercent;
            params.MAXAUTOSIZE = this.getAdaptValue(vmValue.maxAutoSize.value, vmValue.maxAutoSize.unit);
            params.MINAUTOSIZE = this.getAdaptValue(vmValue.minAutoSize.value, vmValue.minAutoSize.unit);
        }
        if (vmValue.spaceselfAdjustingMode === CapacityPolicyE.AUTOEXPANSION) {
            params.AUTOGROWTHRESHOLDPERCENT = vmValue.autoGrowThresholdPercent;
            params.MAXAUTOSIZE = this.getAdaptValue(vmValue.maxAutoSize.value, vmValue.maxAutoSize.unit);
        }

        return params;
    }

    /**
     * 构建CDP下发参数
     * @returns
     */
    buildCdpScheduleParams(): FileSystem {
        const vmValue = this.vm.value;
        const params: FileSystem = {};
        // 如果定时计划名称为'NONE',需要下发'\NONE'
        if (vmValue.addToCdpScheduleSwitch) {
            let selectedCdpScheduleName = '';
            if (SWITCH.IS_SIMPLE_MODE && vmValue.selectedCdpSchedule) {
                selectedCdpScheduleName = vmValue.selectedCdpSchedule.NAME;
            } else {
                selectedCdpScheduleName = vmValue.selectedCdpSchedule;
            }

            if (selectedCdpScheduleName === 'NONE') {
                params.scheduleName = '\\NONE';
            } else {
                params.scheduleName = selectedCdpScheduleName;
            }
        }

        return params;
    }

    getFilesystemParams(): FileSystem {
        const vmValue = this.vm.value;
        let fileSystem: FileSystem = {
            NAME: vmValue.name,
            DESCRIPTION: vmValue.description,
            PARENTID: vmValue.storagePool,
            SNAPSHOTRESERVEPER: vmValue.snapshotReserveper,
            AUTODELSNAPSHOTENABLE: vmValue.autodelSnapshotEnable,
            SPACESELFADJUSTINGMODE: vmValue.spaceselfAdjustingMode,
            CAPACITYTHRESHOLD: vmValue.capacityThreshold,
            securityStyle: vmValue.securityStyle,
            ISSHOWSNAPDIR: vmValue.isShowSnapshotDir,
            ATIME: vmValue.atime,
            ATIMEUPDATEMODE: vmValue.atime ? vmValue.atimeUpdateMode : 4294967295,
            workloadTypeId: Number(vmValue.workloadType),
            isAuditLogFs: this.entry === FileSystemEntryE.AuditLog,
            snapDiffSwitch: vmValue.snapDiffSwitch,
        };

        fileSystem = { ...fileSystem, ...this.buildBasicParams() };

        if (!ProductConfig.isSupportSnapDiffSwitch) {
            delete fileSystem.snapDiffSwitch;
        }

        // 不支持文件系统快照相关字段
        if (ProductConfig.isHideFsSnapshotFeature) {
            // 快照空间比例
            delete fileSystem.SNAPSHOTRESERVEPER;
            // 快照目录是否可见
            delete fileSystem.ISSHOWSNAPDIR;
        }

        //如果安全模式为Native，需下发NAS锁定策略
        if (vmValue.securityStyle === SecurityModeE.Native) {
            fileSystem.nasLockingPolicy = vmValue.nasLockingPolicy;
        }

        fileSystem = { ...fileSystem, ...this.buildSpaceselfAdjustingParams() };

        if (this.entry !== FileSystemEntryE.AuditLog) {
            fileSystem.QUOTASWITCH = this.createQuotaSwitch;
        }

        fileSystem = { ...fileSystem, ...this.buildCdpScheduleParams() };

        if (this.selectedVstore) {
            fileSystem.vstoreId = this.selectedVstore.value;
        }

        // 无license不下发worm参数
        if (this.wormSwitch && this.hasWormLicense) {
            this.collectionWormParams(fileSystem);
        }

        // 非审计日志类型文件系统支持文件系统级别审计日志配置
        if (this.entry !== FileSystemEntryE.AuditLog) {
            fileSystem.auditItems = this.str.getAuditItemParam(vmValue.auditItems);
        }

        return fileSystem;
    }

    /**
     * 构建创建文件系统的命令
     */
    buildCreateFileSystemCmd(): Command[] {
        const cmds: Command[] = [];
        const fileSystem: FileSystem = this.getFilesystemParams();

        // omtask接口
        if (this.checkIfCreateByOmTask()) {
            cmds.push(this.buildOmtaskCmd(fileSystem));
            return cmds;
        } else {
            cmds.push({
                id: 'create_fs',
                name: this.str.createResult(fileSystem.NAME),
                action: this.service.create(fileSystem, { restNameParams: [fileSystem.NAME], doException: false })
                    .pipe(
                        tap(({ data }) => this.closeArgs = { ...this.closeArgs, data }),
                    ),
            });
            if (this.vm.value.smartCachePartition) {
                cmds.push({
                    deps: ['create_fs'],
                    name: this.str.i18n.addFileSystem(fileSystem.NAME, this.selectedSCPName),
                    action: (createRes) => this.service.addAsscociate(this.vm.value.smartCachePartition, createRes.data.ID),
                });
            }
            // 配额
            if (this.createQuotaSwitch && this.quotaDatas.length > 0) {
                const quotas = this.collectionQuotaParams(fileSystem.vstoreId, this.quotaDatas).quotas;
                quotas.forEach((quota) => {
                    // 默认是所有用户/组/所有dtree
                    let name = this.quotaSrt.createResult(Number(quota.QUOTATYPE));
                    // 如果不是所有用户，要传第二个参数
                    if (quota.USRGRPOWNERNAME && quota.USRGRPOWNERNAME !== '*') {
                        name = this.quotaSrt.createResult(Number(quota.QUOTATYPE),
                            this.quotaSrt.userOrGroupRender(quota.USRGRPOWNERNAME, null, quota), Number(quota.NAMETYPE));
                    }

                    cmds.push({
                        deps: ['create_fs'],
                        name,
                        action: (createRes) => this.quotaService.create({
                            ...quota,
                            PARENTID: createRes.data.ID,
                        }),
                    });
                });
            }
            return cmds;
        }
    }

    /**
     * 构建omtask下发命令
     */
    buildOmtaskCmd(fileSystem: FileSystem): Command {
        let nfsParam: FileSystemResource;
        let cifsParam: FileSystemResource;
        let dataTurboParam: FileSystemResource;
        let quotaParam: FileSystemResource;
        let partitionParam: FileSystemResource;
        let remoteStorage: { remoteParentId?: string } = null;
        const vmValue = this.vm.value;

        // NFS
        if (vmValue.NFScreateShare) {
            const vmNFS: NFSShare = vmValue.NFScreateShare[vmValue.NFScreateStyle].share;
            // 权限列表，如果是null，说明没有改动过permissions，只需要下发CIFS的ID就可以了。
            const vmNFSClient: NFSClient[] = vmValue.NFScreateShare[vmValue.NFScreateStyle].permissions;
            nfsParam = this.collectionNFSShareParams(fileSystem.vstoreId, vmNFS, vmNFSClient);
        }

        // CIFS
        if (vmValue.CIFScreateShare && !ProductConfig.isHideCifs) {
            const vmCIFS: CIFSShare = vmValue.CIFScreateShare[vmValue.CIFScreateStyle].share;
            // 权限列表，如果是null，说明没有改动过permissions，只需要下发CIFS的ID就可以了。
            const vmCIFSUser: CIFSClient[] = vmValue.CIFScreateShare[vmValue.CIFScreateStyle].permissions;
            cifsParam = this.collectionCIFSShareParams(fileSystem.vstoreId, vmCIFS, vmCIFSUser);
        }

        // dataTurboParam
        if (this.tempVstoreValue === '0' && vmValue.DataTurbocreateShare) {
            const vmDataTurbo: DataTurboShare = vmValue.DataTurbocreateShare[vmValue.DataTurbocreateStyle].share;
            // 权限列表，如果是null，说明没有改动过permissions，只需要下发CIFS的ID就可以了。
            const vmDataTurboUser: DataTurboClient[] = vmValue.DataTurbocreateShare[vmValue.DataTurbocreateStyle].permissions;
            dataTurboParam = this.collectionDataTurboShareParams(fileSystem.vstoreId, vmDataTurbo, vmDataTurboUser);
        }

        // 双活
        const isHyperMetro = Boolean(this.vstorePairId);
        if (isHyperMetro && this.entry !== FileSystemEntryE.AuditLog) {
            remoteStorage = { remoteParentId: vmValue.remoteParentId };
        }

        // 配额
        if (this.createQuotaSwitch && this.quotaDatas.length > 0) {
            quotaParam = this.collectionQuotaParams(fileSystem.vstoreId, this.quotaDatas);
        }
        // smartPartition
        if (vmValue.smartCachePartition) {
            partitionParam = {
                partitionId: vmValue.smartCachePartition,
            };
        }

        return {
            name: this.str.createResult(fileSystem.NAME),
            action: this.service.createByOmtask(fileSystem.NAME, {
                task_name: SWITCH.IS_SIMPLE_MODE ? FileTaskNameE.createFileShare : undefined,
                fileSystem,
                ...nfsParam,
                ...cifsParam,
                ...dataTurboParam,
                ...remoteStorage,
                ...quotaParam,
                ...partitionParam,
                ...this.service.collectAntivirusData(this.ondemandCard, this.onaccessCard, this.vm, this.collectAntivirusCommonParam()),
            }),
        };
    }

    collectionWormParams(fileSystem: FileSystem): void {
        const wormParam = this.wormConfig.getWormParams();
        Object.assign(fileSystem, wormParam);
    }

    collectionNFSShareParams(vstoreId?: string, vmNFS?: NFSShare, vmNFSClient?: NFSClient[]): FileSystemResource {
        const result: FileSystemResource = {
        };
        const nfs: FileSystemResourceNFS = {
            ID: vmNFS.ID,
            DESCRIPTION: vmNFS.DESCRIPTION,
            CHARACTERENCODING: vmNFS.CHARACTERENCODING,
            ENABLESHOWSNAPSHOT: vmNFS.ENABLESHOWSNAPSHOT,
            vstoreId,
        };

        if (ProductConfig.isHideNfsSnapShot) {
            delete nfs.ENABLESHOWSNAPSHOT;
        }

        if (vmNFS.NAME) {
            nfs.NAME = vmNFS.NAME;
        }

        if (vmNFSClient) {
            delete nfs.ID;
            const clients: NFSClient[] = [];
            vmNFSClient.forEach((data) => {
                clients.push({
                    ACCESSVAL: data.ACCESSVAL,
                    ACCESSKRB5: ProductConfig.isHideKerberos ? undefined : data.ACCESSKRB5,
                    ACCESSKRB5I: ProductConfig.isHideKerberos ? undefined : data.ACCESSKRB5I,
                    ACCESSKRB5P: ProductConfig.isHideKerberos ? undefined : data.ACCESSKRB5P,
                    ALLSQUASH: data.ALLSQUASH,
                    ANONYMOUSID: data.ANONYMOUSID,
                    NAME: data.NAME,
                    ROOTSQUASH: data.ROOTSQUASH,
                    SECURE: data.SECURE,
                    SYNC: data.SYNC,
                    vstoreId,
                });
            });
            nfs.auth = clients;
        }
        result.nfs = [nfs];
        return result;
    }

    collectionCIFSShareParams(vstoreId?: string, vmCIFS?: CIFSShare, vmCIFSUser?: CIFSClient[]): FileSystemResource {
        const result: FileSystemResource = {
        };

        const cifs: FileSystemResourceCIFS = {
            ID: vmCIFS.ID,
            NAME: vmCIFS.NAME,
            DESCRIPTION: vmCIFS.DESCRIPTION,
            ENABLENOTIFY: vmCIFS.ENABLENOTIFY,
            ENABLECA: vmCIFS.ENABLECA,
            ABEENABLE: vmCIFS.ABEENABLE,
            ENABLESHOWSNAPSHOT: vmCIFS.ENABLESHOWSNAPSHOT,
            ENABLESHOWPREVIOUSVERSIONS: vmCIFS.ENABLESHOWPREVIOUSVERSIONS,
            smb3EncryptionEnable: vmCIFS.smb3EncryptionEnable,
            unencryptedAccess: vmCIFS.unencryptedAccess,
            vstoreId,
            OFFLINEFILEMODE: vmCIFS.OFFLINEFILEMODE,
        };
        if (vmCIFSUser) {
            delete cifs.ID;
            const users: CIFSClient[] = [];
            vmCIFSUser.forEach((data) => {
                users.push({
                    NAME: data.NAME,
                    DOMAINTYPE: Number(data.DOMAINTYPE),
                    PERMISSION: Number(data.PERMISSION),
                    vstoreId,
                });
            });
            cifs.auth = users;
        }
        result.cifs = [cifs];
        return result;
    }

    collectionDataTurboShareParams(vstoreId?: string, dataTurboShare?: DataTurboShare,
        dataTurboUser?: DataTurboClient[]): FileSystemResource {
        const result: FileSystemResource = {};

        const dataTurbo: DataTurboShare = {
            description: dataTurboShare.description,
        };

        if (dataTurboUser) {
            const users: DataTurboClient[] = [];

            dataTurboUser.forEach((data) => {
                users.push({
                    userName: data.userName,
                    permission: Number(data.permission),
                });
            });

            dataTurbo.auth = users;
        }
        result.dataturbo = [dataTurbo];
        return result;
    }

    collectionQuotaParams(vstoreId: string, datas: Quota[]): FileSystemResource {
        const result: FileSystemResource = {
        };
        const quotas: Quota[] = [];

        datas.forEach((item) => {
            const quota: Quota = {
                PARENTTYPE: item.PARENTTYPE,
                QUOTATYPE: item.QUOTATYPE,
                SPACEUNITTYPE: item.SPACEUNITTYPE,
                DOMAINTYPE: item.DOMAINTYPE,
                USRGRPTYPE: item.USRGRPTYPE,
                NAMETYPE: item.NAMETYPE,
                USRGRPOWNERNAME: item.USRGRPOWNERNAME,
                FILEHARDQUOTA: item.FILEHARDQUOTA,
                FILESOFTQUOTA: item.FILESOFTQUOTA,
                SPACEHARDQUOTA: item.SPACEHARDQUOTA,
                SPACESOFTQUOTA: item.SPACESOFTQUOTA,
                vstoreId,
            };
            if (isUndefined(quota.DOMAINTYPE)) {
                delete quota.DOMAINTYPE;
            }
            if (isUndefined(quota.USRGRPTYPE)) {
                delete quota.USRGRPTYPE;
            }
            if (isUndefined(quota.NAMETYPE)) {
                delete quota.NAMETYPE;
            }
            if (isUndefined(quota.USRGRPOWNERNAME)) {
                delete quota.USRGRPOWNERNAME;
            }
            if (isUndefined(quota.FILEHARDQUOTA)) {
                delete quota.FILEHARDQUOTA;
            }
            if (isUndefined(quota.FILESOFTQUOTA)) {
                delete quota.FILESOFTQUOTA;
            }
            if (isUndefined(quota.SPACEHARDQUOTA)) {
                delete quota.SPACEHARDQUOTA;
            }
            if (isUndefined(quota.SPACESOFTQUOTA)) {
                delete quota.SPACESOFTQUOTA;
            }
            quotas.push(quota);
        });
        result.quotas = quotas;
        return result;
    }

    /**
     * 合法性校验
     */
    validate(): Observable<boolean> {
        return this.vm.validateOb().pipe(
            tap((valid) => {
                // 复制模式无应用类型数据校验不通过，展开高级选项，暴露出错误项
                if (!valid && this.data && !this.data.workloadTypeId) {
                    this.advanced = true;
                }
            }),
            // 双活模式时双活license校验，需要在高危提示之前
            flatMap((valid) => {
                if (this.vstorePairId && valid) {
                    const licenses = [LicenseKey.FileHyperMetro];
                    if (this.fsHyperMetroDomain &&
                        (this.fsHyperMetroDomain.workMode === WorkModeE.HyperMetro ||
                            this.fsHyperMetroDomain.workMode === WorkModeE.AP)) {
                        if (!this.permissionService.isLicenseExist(licenses)) {
                            return this.showNoLicensePropmt(this.i18nService.get('common.EnumDefine_LicenseFuncFeatureType_72'))
                                .pipe(map(() => true), catchError(() => of(false)));
                        }

                        return this.permissionService.checkLicense(licenses).pipe(map(() => true), catchError(() => of(false)));
                    }
                }
                return of(valid);
            }),
            // worm文件系统的校验
            flatMap((valid) => {
                if (valid && this.wormConfig && this.wormSwitch) {
                    return this.wormConfig.vm.validateOb();
                } else {
                    return of(valid);
                }
            }),
        );
    }

    /**
     * 复写exec，如果走omtask，就不走cmd的流程。
     * 如果需要开启全局worm时钟，需要先开启才能创建文件系统
     */
    exec(): void {
        const needInitWormClock = this.wormSwitch && this.hasWormClockW && !this.wormClockInited && this.wormConfig && this.wormConfig.vm.value.initWormClock;

        // 开启worm配置，且未初始化worm时钟时，且选中初始化选项，需要先初始化全局时钟，再创建文件系统
        if (needInitWormClock) {
            this.initWormClock();
        } else {
            this.doExec();
        }
    }

    initWormClock(): void {
        const wormClockTime = this.timeService.currTime;
        this.service.initWormClock(wormClockTime).subscribe(
            () => {
                this.wormClockInited = true;
                this.doExec();
            },
        );
    }

    doExec(): void {
        if (this.checkIfCreateByOmTask()) {
            const cmds = this.buildCmds();
            if (cmds && cmds.length === 1) {
                this.loading = true;
                (cmds[0].action as Observable<HttpResponse>).pipe(finalize(() => {
                    this.loading = false;
                })).subscribe(({ data }) => {
                    this.closeArgs.refresh = true;
                    // taskId带过去便于监听该任务的创建成功
                    this.closeArgs.data = { taskId: data.taskId };
                    this.hide();
                });
            } else {
                this.hide();
            }
        } else {
            super.exec();
        }
    }

    getCreateMsgPrompt(): PromptConfigFull {
        return {
            id: 'prompt-create-file-system-createMsg',
            opType: 'warning',
            title: this.i18n.createTitle,
            content: this.i18n.createMsg,
            width: 700,
        };
    }

    prompt(): PromptConfigFull[] {
        const prompts: PromptConfigFull[] = [];
        const CAPACITY_3GB = this.utilService.adaptUnit(3, CAPACITY_UNIT.GB, CAPACITY_UNIT.SECTOR).value;
        const fsCapacity = this.utilService.adaptUnit(
            this.vm.value.capacity?.value,
            this.vm.value.capacity?.unit,
            CAPACITY_UNIT.SECTOR,
        ).value;
        const vmValue = this.vm.value;

        // 创建文件系统容量不大于3GB时，需要高危提示
        if (fsCapacity <= CAPACITY_3GB) {
            prompts.push(this.getCreateMsgPrompt());
        }

        if (vmValue.CIFScreateShare) {
            const vmCIFS: CIFSShare = vmValue.CIFScreateShare[vmValue.CIFScreateStyle].share;

            if (this.utilService.toBool(vmCIFS.smb3EncryptionEnable)) {
                prompts.push({
                    id: 'prompt-create-file-system-SMB3EncryptEnabledTip',
                    opType: 'warning',
                    title: this.i18n.createCIFS,
                    content: this.i18n.SMB3EncryptEnabledTip,
                    width: 700,
                });
            }

            if (this.utilService.toBool(vmCIFS.unencryptedAccess)) {
                prompts.push({
                    id: 'prompt-create-file-system-enableUnencryptedAccessTip',
                    opType: 'warning',
                    title: this.i18n.createCIFS,
                    content: this.i18n.enableUnencryptedAccessTip,
                    width: 700,
                });
            }

            if (vmCIFS.OFFLINEFILEMODE !== OffLineFileModeE.None) {
                prompts.push({
                    id: 'prompt-create-fs-modeNotNoneTip',
                    opType: 'info',
                    title: this.i18n.createCIFS,
                    content: this.i18n.modeNotNoneTip,
                    width: 700,
                });
            }
        }

        // 需要初始化时钟时，需要有高危提示
        if (this.needInitClocksPrompt()) {
            prompts.push({
                id: 'prompt-create-file-system-initClocksContent',
                opType: 'warning',
                title: this.i18n.worm.initClocks,
                content: this.i18n.worm.initClocksContent,
                width: 700,
            });
        }

        // 开启自动删除时，需要有高危提示
        if (this.wormSwitch && this.wormConfig && this.wormConfig.vm.value.wormAutoDel) {
            prompts.push({
                id: 'prompt-create-file-system-autoDelContent',
                opType: 'warning',
                title: this.i18n.createTitle,
                content: this.i18n.worm.autoDelContent,
                width: 700,
            });
        }

        // 创建审计日志文件系统时，如果租户是双活租户，需要有高危提示
        if (this.entry === FileSystemEntryE.AuditLog && this.vstorePairId) {
            prompts.push({
                id: 'prompt-create-file-system-createAuditLogHighRiskTip',
                opType: 'warning',
                title: this.i18n.createTitle,
                content: this.i18n.createAuditLogHighRiskTip,
                width: 700,
            });
        }

        // 配置了审计项，需要有高危提示
        if (this.str.getAuditItemParam(vmValue.auditItems) > 0) {
            prompts.push({
                id: 'prompt-create-file-system-createAuditItemsHighRiskTip',
                opType: 'warning',
                title: this.i18n.createTitle,
                content: this.i18n.createAuditItemsHighRiskTip,
                width: 700,
            });
        }
        // 开启了vaai，需要有高危提示
        if (this.vm.contains('vaaiSwitch') && this.vm.value.vaaiSwitch) {
            prompts.push({
                id: 'prompt-create-file-system-vaaiMsg',
                opType: 'warning',
                title: this.i18n.createTitle,
                content: this.i18n.vaaiMsg,
                width: 700,
            });
        }

        // 存储池关闭了全局重删
        if (Number(this.selectedWorkloadType?.distAlg) === DistAlgE.DIRECTORY_BALANCE_MODE && this.selectedStoragePool?.isSupportGlobalDedup === 'false') {
            prompts.push({
                id: 'prompt-create-file-system-disable—global-dedup',
                opType: 'warning',
                title: this.i18n.createTitle,
                content: this.i18n.disableGlobalDedupMsg,
                width: 700,
            });
        }

        return prompts;
    }

    /**
     * 是否需要初始化时钟高危提示
     */
    needInitClocksPrompt(): boolean {
        return this.wormSwitch && this.hasWormClockW && !this.wormClockInited && this.wormConfig && this.wormConfig.vm.value.initWormClock;
    }

    permissionCheck() {
        return (): {
            permission: boolean;
        } => {
            const isHyperMetroVstore = this.selectedVstore && this.isHyperMetroVstore(this.selectedVstore.data);

            if (isHyperMetroVstore && !this.permissionService.checkShow(['task_W', 'hyper_metro_pair_W', 'hyper_metro_domain_R'])) {
                return { permission: true };
            }

            return null;
        };
    }

    isHyperMetroVstore(vstore: Vstore): boolean {
        return Boolean(Number(vstore.metroCount));
    }

    /**
     * 判断是否显示内置CDP计划
     */
    supportCdpScheduleInit(): void {
        this.supportCdpSchedule = this.permissionService.checkShow(['@hyper_cdp_W', 'fs_snapshot_W'], [LicenseKey.HyperCDP]);

        if (!this.supportCdpSchedule) {
            this.vm.get('selectedCdpSchedule').disable();
        }
    }

    /**
     * 检查全局worm时钟状态
     */
    checkCLockStatus(): void {
        this.clockLoading = true;
        this.service.getWormClock().pipe(
            finalize(() => {
                this.clockLoading = false;
            }),
        ).subscribe(
            ({ data }) => {
                this.wormClockInited = Boolean(data.wormGlobalClock);
            },
            () => {
                this.wormClockInited = false;
            },
        );
    }

    wormSwitchChange(value: boolean): void {
        // 检查worm时钟的权限
        if (value) {
            this.checkWormParam();
            if (this.hasWormClockR && !this.wormClockInited) {
                this.checkCLockStatus();
            }
        }
    }

    /**
     * 1、审计日志文件系统不支持
     * 2、无wormlicense
     */
    checkWormShow = (vstorePairId: string, advanced: boolean): boolean => {
        const isAuditLog = this.entry === FileSystemEntryE.AuditLog;
        return !advanced || isAuditLog || !this.hasWormLicense;
    };

    ngOnDestroy(): void {
        if (this.taskSub$) {
            this.taskSub$.unsubscribe();
        }
    }

    /**
     * 重置远端存储池下拉框的值
     */
    resetPoolControlValue(): void {
        const poolControl = this.vm.get('storagePool') as FormControl;

        if (poolControl.status === 'VALID') {
            poolControl.markAsPristine();
            poolControl.markAsUntouched();
        }

        poolControl.patchValue('');
    }
}
